import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ — European Wig Luxury" },
      { name: "description", content: "Common questions about virgin European hair, cap construction, care, sizing, and insurance reimbursement for cranial prostheses." },
      { property: "og:title", content: "European Wig Luxury FAQ" },
      { property: "og:description", content: "Virgin European hair, cap types, care, sizing, and insurance." },
      { property: "og:url", content: "https://europeanwigluxury.com/faq" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: groups.flatMap((g) =>
            g.items.map((it) => ({
              "@type": "Question",
              name: it.q,
              acceptedAnswer: { "@type": "Answer", text: it.a },
            })),
          ),
        }),
      },
    ],
  }),
  component: FAQ,
});

const groups = [
  {
    id: "hair",
    title: "Hair & sourcing",
    items: [
      {
        q: "What does 'virgin European hair' actually mean?",
        a: "Virgin means the hair has never been chemically processed — no dye, no bleach, no perm, no acid bath. European means the hair is of European origin, which gives it a specific fine-to-medium density and natural range of blonde-to-brunette shades that behaves differently from other origins. The cuticles are kept aligned root to tip so the hair lies flat, reflects light naturally, and resists tangling.",
      },
      {
        q: "Why don't you use hair 'grades' like 10A or 12A?",
        a: "Those grades are unregulated marketing invented by resellers. There is no industry body that certifies what 10A means, so one vendor's 10A can be another's 6A. We describe our hair by what actually matters: origin, virgin status, cuticle alignment, and construction.",
      },
      {
        q: "What's cuticle alignment?",
        a: "Each hair strand has a cuticle that runs in one direction, like scales on a fish. When cuticles are all aligned root-to-tip, the hair lies flat, reflects light naturally, and resists tangling. When hair is collected from a floor, blended, or acid-processed, the cuticles are stripped or reversed — the hair looks fine at first but mats quickly.",
      },
    ],
  },
  {
    id: "caps",
    title: "Cap construction",
    items: [
      { q: "What's a monofilament (mono) top?", a: "A soft, breathable mesh forms the crown of the cap. Each hair is knotted into it individually, so you can part the hair anywhere on top and the parting looks like scalp." },
      { q: "What's a silk / French top?", a: "A layer of thin silk sits above the mono base, hiding the knots underneath. The result is the most realistic 'growing out of the scalp' illusion — often preferred by religious clients and anyone who wants the most invisible top possible. All European Wig Luxury pieces use a silk-top construction." },
      { q: "Do you make lace-front or full-lace wigs?", a: "Not currently. Our short-length pieces are silk-top constructions — a hand-tied silk crown over a soft cap. It's the most invisible option for a natural-looking parting and is what we've committed to." },
    ],
  },
  {
    id: "care",
    title: "Care & lifespan",
    items: [
      { q: "How long will my wig last?", a: "Cared for properly, our short virgin European hair pieces routinely last 3–5+ years of regular wear. Lower-cost processed pieces typically break down in 6–12 months as the chemical treatment wears off." },
      { q: "How often should I wash it?", a: "Every 8–15 wears is typical, depending on your climate and product use. Full instructions are in our Care Guide." },
    ],
  },
  {
    id: "sizing",
    title: "Sizing & fit",
    items: [
      { q: "What size are your caps?", a: "Our standard cap fits head circumferences of about 21\" to 22.5\" using an adjustable strap. If you fall outside that range or need a custom fit, contact us and we can help you find the right fit." },
      { q: "How do I measure my head?", a: "Wrap a soft measuring tape around your head just above the ears and across the mid-forehead. That's your circumference." },
    ],
  },
  {
    id: "returns",
    title: "Returns",
    items: [
      {
        q: "What is your return policy?",
        a: "We accept returns within 7 days of purchase, subject to a 20% restocking fee. Return shipping is the customer's responsibility. See the full Return Policy page for the complete terms you'll be asked to acknowledge at checkout.",
      },
      {
        q: "When can a return be refused?",
        a: "A return is refused if the wig has been altered, cut, colored, washed, or restyled; if it smells of perfume, cologne, hairspray, smoke, food, or any other odor; if it is torn, ripped, or damaged; or if it arrives without the original packaging and hairnet. Every returned piece is inspected on arrival.",
      },
      {
        q: "Do you accept custom orders?",
        a: "No. We do not accept custom-color, custom-cut, or made-to-measure orders. Every piece is sold as shown.",
      },
      {
        q: "How do I start a return?",
        a: "Email care@europeanwigluxury.com within 7 days of your purchase with your order number and reason for return. We'll confirm eligibility and send you the return address.",
      },
      {
        q: "How long do refunds take?",
        a: "Once we receive and approve the return, refunds are issued to the original payment method within 5–7 business days, minus the 20% restocking fee and any original shipping charges.",
      },
    ],
  },
  {
    id: "insurance",
    title: "Insurance & reimbursement",
    items: [
      {
        q: "Do you bill my insurance directly?",
        a: "No. We are out-of-network with all insurance carriers, so we do not bill insurance on your behalf. You pay us at checkout, and we provide the paperwork you need to file the claim yourself with your insurer, HSA, or FSA.",
      },
      {
        q: "How does the reimbursement process work, step by step?",
        a: "1) Ask your doctor for a prescription for a 'cranial prosthesis' (not a 'wig') citing your diagnosis. 2) Purchase your piece from us. 3) Email us your order number and request a Cranial Prosthesis receipt — we'll email you an itemized receipt with HCPCS code A9282 and our NPI number (1609704063, taxonomy 335E00000X, Prosthetic/Orthotic Supplier). 4) Submit the prescription plus our receipt to your insurance company, HSA, or FSA administrator. 5) Reimbursement is paid directly to you by your insurer. Coverage varies by plan.",
      },
      {
        q: "Why do I need a prescription that says 'cranial prosthesis'?",
        a: "The word matters. Most insurers, HSAs, and FSAs will not reimburse a 'wig' but will reimburse a 'cranial prosthesis' for medical hair loss (alopecia, chemotherapy, trichotillomania, etc.). Your doctor should write the words 'cranial prosthesis' along with your diagnosis code — we can share a sample letter your doctor can adapt.",
      },
      {
        q: "What is on the receipt you provide?",
        a: "The itemized receipt includes: our business name, our NPI (1609704063) and taxonomy code (335E00000X — Prosthetic/Orthotic Supplier), HCPCS code A9282, a description of the item as a cranial prosthesis, the price you paid, and the date of purchase. This is the format most insurers, HSA, and FSA administrators require.",
      },
      {
        q: "Can I use HSA or FSA funds?",
        a: "Yes. HSA and FSA plans generally reimburse cranial prostheses when accompanied by a physician's prescription. Submit our itemized receipt and your prescription to your plan administrator.",
      },
      {
        q: "Will I definitely get reimbursed?",
        a: "We can't guarantee any specific claim. Reimbursement depends entirely on your individual insurance plan's benefits, deductible, and rules. We strongly recommend calling your insurer before purchase to confirm they cover cranial prostheses (HCPCS A9282) and what documentation they require.",
      },
      {
        q: "How do I request the receipt?",
        a: "Tick the 'Request a cranial prosthesis receipt' box in your bag before checkout, or email care@europeanwigluxury.com with your order number after purchase. We'll email the itemized receipt to you within one business day.",
      },
    ],
  },
];

function FAQ() {
  const routerHash = useRouterState({ select: (s) => s.location.hash });
  // Hash is client-only (never sent to the server), so gate hash-driven
  // rendering behind a hydration flag to avoid SSR/CSR mismatches.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);
  const hash = hydrated ? routerHash : "";

  useEffect(() => {
    if (!hash) return;
    // Wait one tick so the accordion default value is applied before scrolling
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <section className="border-b border-border/70 bg-secondary/40">
        <div className="container-luxe py-16 md:py-20">
          <p className="eyebrow">Answers</p>
          <h1 className="mt-3 font-display text-4xl md:text-6xl">Frequently asked</h1>
        </div>
      </section>
      <section className="container-luxe py-16 md:py-20">
        <div className="mx-auto max-w-3xl space-y-16">
          {groups.map((g) => {
            const isTarget = hash === g.id;

            return (
              <div key={g.id} id={g.id} className="scroll-mt-24">
                <h2 className="font-display text-3xl md:text-4xl">{g.title}</h2>
                {isTarget ? (
                  <Accordion
                    type="multiple"
                    className="mt-6"
                    defaultValue={g.items.map((_, i) => `${g.id}-${i}`)}
                  >
                    {g.items.map((it, i) => (
                      <AccordionItem key={i} value={`${g.id}-${i}`}>
                        <AccordionTrigger className="text-left font-display text-xl md:text-2xl py-5">{it.q}</AccordionTrigger>
                        <AccordionContent className="text-base md:text-lg leading-relaxed text-muted-foreground">{it.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <Accordion type="single" collapsible className="mt-6">
                    {g.items.map((it, i) => (
                      <AccordionItem key={i} value={`${g.id}-${i}`}>
                        <AccordionTrigger className="text-left font-display text-xl md:text-2xl py-5">{it.q}</AccordionTrigger>
                        <AccordionContent className="text-base md:text-lg leading-relaxed text-muted-foreground">{it.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

